import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { fmtMoney } from "../components/Format";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Portfolio() {
  const { mode } = useAuth();
  const [p, setP] = useState(null);
  const [stats, setStats] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/portfolio"), api.get("/trades/stats", { params: { mode } })]);
      setP(a.data); setStats(b.data);
    } catch { /* ignore transient refresh failures */ }
  }, [mode]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
  }, [refresh]);

  async function reset() {
    if (!window.confirm("Reset paper portfolio to $100,000 and clear paper trade history?")) return;
    try {
      await api.post("/portfolio/reset");
      toast.success("Paper portfolio reset");
      refresh();
    } catch { toast.error("Reset failed"); }
  }

  const equity = mode === "live" ? p?.equity_live : p?.equity_paper;
  const cash = mode === "live" ? p?.cash_live : p?.cash_paper;
  const pnl = mode === "live" ? p?.realized_pnl_live : p?.realized_pnl_paper;

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Holdings &amp; Performance</div>
          <h1 className="text-3xl font-[Chivo] tracking-tight">Portfolio</h1>
        </div>
        {mode === "paper" && (
          <Button data-testid="reset-portfolio" onClick={reset} variant="outline" className="border-border bg-transparent hover:bg-[#141414]">
            <RefreshCw size={14} className="mr-2" /> Reset Paper Portfolio
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        <Big label="Total Equity" value={`$${fmtMoney(equity ?? 0)}`} testid="port-equity" />
        <Big label="Available Cash" value={`$${fmtMoney(cash ?? 0)}`} testid="port-cash" />
        <Big label="Realized PnL" value={`${(pnl ?? 0) >= 0 ? "+" : ""}$${fmtMoney(pnl ?? 0)}`} color={(pnl ?? 0) >= 0 ? "#00E676" : "#FF3B30"} testid="port-pnl" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-border bg-[#0F0F0F]" data-testid="perf-stats">
          <div className="px-4 py-3 border-b border-border label-xs">Performance ({mode})</div>
          <div className="p-4 space-y-3 text-sm">
            <Row k="Total Trades" v={<span className="num">{stats?.trades ?? 0}</span>} />
            <Row k="Wins / Losses" v={<span className="num">{stats?.wins ?? 0} / {(stats?.trades ?? 0) - (stats?.wins ?? 0)}</span>} />
            <Row k="Win Rate" v={<span className="num">{(stats?.win_rate ?? 0).toFixed(2)}%</span>} />
            <Row k="Volume Traded" v={<span className="num">${fmtMoney(stats?.volume ?? 0, 0)}</span>} />
            <Row k="Fees Paid" v={<span className="num">${fmtMoney(stats?.fees ?? 0, 2)}</span>} />
          </div>
        </div>
        <div className="border border-border bg-[#0F0F0F]" data-testid="account-info">
          <div className="px-4 py-3 border-b border-border label-xs">Account Allocation</div>
          <div className="p-4 space-y-3 text-sm">
            <Row k="Mode" v={<span className="font-[Chivo]">{mode.toUpperCase()}</span>} />
            <Row k="Paper Cash" v={<span className="num">${fmtMoney(p?.cash_paper ?? 0)}</span>} />
            <Row k="Live Cash" v={<span className="num">${fmtMoney(p?.cash_live ?? 0)}</span>} />
            <Row k="Total Lifetime Trades" v={<span className="num">{p?.total_trades ?? 0}</span>} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Big({ label, value, color, testid }) {
  return (
    <div className="bg-[#0F0F0F] px-6 py-6" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className="num text-3xl font-[Chivo] tracking-tight mt-2" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-neutral-400">{k}</span>{v}</div>;
}
